"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="224157" className="hidden md:flex gap-6">
        <div className="h-6 w-16 bg-muted animate-pulse rounded-full" />
        <div className="h-6 w-20 bg-muted animate-pulse rounded-full" />
        <div className="h-6 w-24 bg-muted animate-pulse rounded-full" />
      </div>;
  }
  const stemWaveItem = menuItems.find(item => item.label === "StemWave");
  const otherItems = menuItems.filter(item => item.label !== "StemWave");
  const sortedMenuItems = stemWaveItem ? [stemWaveItem, ...otherItems] : menuItems;
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-6">
        {sortedMenuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-foreground font-default font-semibold text-base hover:text-ring transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuContent({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 py-4">
        <div className="h-10 w-full bg-muted animate-pulse rounded-lg" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-lg" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-lg" />
      </div>;
  }
  const stemWaveItem = menuItems.find(item => item.label === "StemWave");
  const otherItems = menuItems.filter(item => item.label !== "StemWave");
  const sortedMenuItems = stemWaveItem ? [stemWaveItem, ...otherItems] : menuItems;
  return <div className="flex flex-col gap-4 py-4 max-h-[80vh] overflow-y-auto">
      <nav className="flex flex-col gap-2">
        {sortedMenuItems.map((item, i) => <Link key={item.id} to={i === 0 ? item.href?.replace(/^\/+/, '') || "#" : item.href || "#"} className="text-foreground font-default font-semibold text-lg py-3 border-b border-border/50 hover:text-ring transition-colors" onClick={onNavigate} data-index={i}>
            {item.label}
          </Link>)}
      </nav>
      <div className="pt-4">
        <Button variant="destructive" size="lg" className="w-full font-default font-semibold bg-ring hover:bg-ring/90 text-primary-foreground rounded-full shadow-lg shadow-ring/20" asChild onClick={onNavigate}>
          <Link to="https://drkaszubaprimarycare.com/contact-us/">Book Online</Link>
        </Button>
      </div>
    </div>;
}
export default function GatewayNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="clinic-header" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-[12px] border-b border-border shadow-md" : "bg-background border-b border-transparent"}`}>
      <MenuProvider menu_id="16" className="container mx-auto px-4 md:px-6 lg:px-8 h-20 flex items-center justify-between">
        
        <Link to="#" className="flex-shrink-0 z-50 flex items-center">
          <span className="text-xl font-bold tracking-tight text-primary font-default">
            Dr. Kaszuba Primary Care
          </span>
        </Link>

        
        <DesktopMenu />

        
        <div className="hidden lg:flex flex-col text-[10px] xl:text-[11px] leading-tight text-muted-foreground border-l border-border pl-4 xl:pl-6 ml-4 xl:ml-6">
          <div><span className="font-bold text-primary">Phone:</span> <span className="text-foreground">727-216-0505</span></div>
          <div><span className="font-bold text-primary">Address:</span> <span className="text-foreground font-medium">1803 Briar Creek Blvd, Safety Harbor, FL 34695</span></div>
          <div><span className="font-bold text-primary">Hours:</span> <span className="text-foreground">Mon–Fri 9am–5pm | Sat 10am–1pm | Sun Closed</span></div>
        </div>

        
        <div className="hidden md:flex flex-shrink-0">
          <Button variant="destructive" className="font-default font-semibold bg-ring hover:bg-ring/90 text-primary-foreground rounded-full shadow-lg shadow-ring/20 transition-all hover:scale-105" asChild>
            <Link to="https://drkaszubaprimarycare.com/contact-us/">Book Online</Link>
          </Button>
        </div>

        
        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent hover:text-accent-foreground">
              <Menu className="size-6" />
              <span className="sr-only">Toggle navigation menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="top" className="w-full border-b border-border bg-background/95 backdrop-blur-md max-h-screen overflow-y-auto">
            <MobileMenuContent onNavigate={() => setIsMobileMenuOpen(false)} />
          </SheetContent>
        </Sheet>
      </MenuProvider>
    </section>;
}